/**
 * services/proposals/proposals.ts
 * AI proposal review + derivation. Reads/writes fall back to the demo seed via
 * withFallback (writes resolve as optimistic success in demo mode).
 */
import { axiosInstance, unwrap, withFallback } from '../client';
import {
  Upsell_Proposals_Url,
  Upsell_Proposal_Approve_Url,
  Upsell_Proposal_Reject_Url,
  Upsell_Proposal_Restore_Url,
  Upsell_Proposal_Delete_Url,
  Upsell_Derivation_Run_Url,
} from '../../api-endpoints/endpoints';
import { Proposal } from '../../schema/proposal';
import { RuleBody } from '../../schema/rules/rule';
import { Seed } from '../_demo/seed';
import { getDemoVertical } from '../_demo/verticalStore';

interface WireProposal {
  id: string;
  motion: string;
  status?: string;
  confidence?: number;
  intent?: string;
  rationale?: string;
  category_examples?: Array<{ name: string; items: string[] }>;
  stale?: boolean;
  stale_reason?: string;
  proposed_rule?: {
    trigger?: { all?: Array<Record<string, unknown>>; any?: Array<Record<string, unknown>> };
    recommend?: { from?: { category?: string; relation?: string }; select?: string };
    constraints?: { max_price_delta_pct?: number };
    phrasing?: { template?: string };
  };
}

function toProposal(w: WireProposal): Proposal {
  // Pull the trigger / exclude categories out of the rule's condition list so
  // "Edit" can open the Builder pre-filled with the proposal's real shape.
  const conds = [...(w.proposed_rule?.trigger?.all ?? []), ...(w.proposed_rule?.trigger?.any ?? [])];
  let triggerCategory: string | undefined;
  let excludeCategory: string | undefined;
  for (const c of conds) {
    if (typeof c.cart_contains_category === 'string') triggerCategory = c.cart_contains_category;
    if (typeof c.cart_not_contains_category === 'string') excludeCategory = c.cart_not_contains_category;
  }
  return {
    id: w.id,
    motion: w.motion,
    ...(w.status ? { status: w.status } : {}),
    confidence: w.confidence ?? 0,
    intent: w.intent ?? '',
    rationale: w.rationale ?? '',
    from: {
      category: w.proposed_rule?.recommend?.from?.category,
      relation: w.proposed_rule?.recommend?.from?.relation,
    },
    triggerCategory,
    excludeCategory,
    phrasing: w.proposed_rule?.phrasing?.template,
    categoryExamples: w.category_examples ?? [],
    select: w.proposed_rule?.recommend?.select ?? 'curated_order',
    maxPriceDelta: w.proposed_rule?.constraints?.max_price_delta_pct ?? 50,
    stale: w.stale ?? false,
    ...(w.stale_reason ? { staleReason: w.stale_reason } : {}),
  };
}

export function listProposals(): Promise<Proposal[]> {
  return withFallback(
    async () => {
      // No status filter → backend returns pending + rejected (the reviewable set),
      // which the screen splits into Active / Deactivated / Rejected tabs.
      const { data } = await axiosInstance.get(Upsell_Proposals_Url);
      return unwrap<WireProposal[]>(data).map(toProposal);
    },
    () => Seed.proposals(getDemoVertical()),
  );
}

export function approveProposal(id: string, rule?: RuleBody): Promise<void> {
  return withFallback(
    async () => {
      await axiosInstance.post(Upsell_Proposal_Approve_Url(id), rule ? { rule } : {});
    },
    () => undefined,
  );
}

export function rejectProposal(id: string): Promise<void> {
  return withFallback(
    async () => {
      await axiosInstance.post(Upsell_Proposal_Reject_Url(id));
    },
    () => undefined,
  );
}

/** Un-reject → back to pending (Active/Deactivated). */
export function restoreProposal(id: string): Promise<void> {
  return withFallback(
    async () => {
      await axiosInstance.post(Upsell_Proposal_Restore_Url(id));
    },
    () => undefined,
  );
}

/** Soft-delete → hidden from all tabs, retained in the DB. */
export function deleteProposal(id: string): Promise<void> {
  return withFallback(
    async () => {
      await axiosInstance.post(Upsell_Proposal_Delete_Url(id));
    },
    () => undefined,
  );
}

export interface DerivationResult {
  run_id: string;
  proposals_count: number;
}

export function runDerivation(): Promise<DerivationResult> {
  return withFallback(
    async () => {
      // Derivation makes a comprehensive LLM call that routinely runs longer than
      // the default 30s client timeout — the request was being canceled at 30s
      // even though the server completed and saved the proposals. Give this one
      // call a generous timeout so the FE gets the real result (and count).
      const { data } = await axiosInstance.post(Upsell_Derivation_Run_Url, undefined, { timeout: 180_000 });
      return unwrap<DerivationResult>(data);
    },
    () => ({ run_id: 'seed-run', proposals_count: Math.min(2, Seed.proposals(getDemoVertical()).length) }),
  );
}
